/**
 * PATH: packages/ui/src/kanban/components/card-detail/attachmentUploadLimits.ts
 * FILE_ID: UI-KANBAN-LIB-ATTACH-LIMITS-001
 * PURPOSE: The ONE shared client-side size gate + per-file failure
 *          aggregation for every upload path that hits the card
 *          attachments endpoint (POST /cards/<id>/attachments/).
 * WHY:     BUG-2026-07-09-KANBAN-ATTACH-UPLOAD-NO-CLIENT-GUARDS — the
 *          50 MB cap lived only server-side
 *          (email_kanban/services/card_attachments.py), so oversized
 *          files burned a full upload before failing; and the multi-file
 *          loop overwrote the error per file, hiding which files failed.
 *          Invariant: every client upload path to card attachments
 *          validates size against THIS constant and reports failures
 *          per-file by name.
 * RELATED:
 *   - CardAttachmentsSection.tsx: pick + drag-drop uploads (consumer).
 *   - packages/kanban-host/src/KanbanDocumentOverlay.tsx: follow-up
 *     page-PNG uploads (consumer via @quotech/ui/kanban).
 */
import { formatFileSize } from '../../../components/email/attachmentClassify'

/** Mirrors the BE cap in email_kanban/services/card_attachments.py. */
export const MAX_CARD_ATTACHMENT_BYTES = 50 * 1024 * 1024

/** "big.zip is 512 MB — the limit is 50 MB." */
export function oversizedAttachmentMessage(name: string, sizeBytes: number): string {
  return `${name} is ${formatFileSize(sizeBytes)} — the limit is ${formatFileSize(
    MAX_CARD_ATTACHMENT_BYTES,
  )}.`
}

/** True when the file must be rejected client-side (strictly over the cap —
 *  a file of exactly MAX_CARD_ATTACHMENT_BYTES is allowed, matching the BE). */
export function isOversizedAttachment(sizeBytes: number): boolean {
  return sizeBytes > MAX_CARD_ATTACHMENT_BYTES
}

export interface AttachmentUploadFailure {
  filename: string
  message: string
}

/** One aggregate, per-file-named error line for a batch of failures. */
export function summarizeUploadFailures(
  failures: AttachmentUploadFailure[],
  totalCount: number,
): string | null {
  if (failures.length === 0) return null
  if (failures.length === 1) return failures[0].message
  const names = failures.map((f) => f.filename).join(', ')
  return `${failures.length} of ${totalCount} files failed to upload: ${names}. ${failures
    .map((f) => f.message)
    .join(' ')}`
}
